import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  SmartToy as RobotIcon,
  People as UserIcon,
  Route as TourIcon,
  Folder as ContentIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../store/hooks';
import { apiService as api } from '../shared/services/api';
import { useRecentActivities } from '../modules/reports/hooks/useReports';

interface ActivityItem {
  id?: number | string;
  type: string;
  message: string;
  timestamp?: string;
  status?: string;
}

const getCountFromResponse = (value: unknown): number => {
  if (Array.isArray(value)) {
    return value.length;
  }
  if (typeof value === 'object' && value && 'results' in value) {
    const results = (value as { results?: unknown[] }).results;
    return Array.isArray(results) ? results.length : 0;
  }
  return 0;
};

const DashboardOverview: React.FC = () => {
  // Data hooks
  const { user } = useAppSelector(state => state.auth);
  const [systemStats, setSystemStats] = useState({
    totalRobots: 0,
    totalUsers: 0,
    totalTours: 0,
    totalContent: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get recent activities from NHB API
  const { data: activitiesData, loading: activitiesLoading } = useRecentActivities(10);
  const recentActivities = ((activitiesData as { activities?: ActivityItem[] } | null)?.activities || []) as
    | ActivityItem[]
    | [];

  // Fetch real data from APIs
  useEffect(() => {
    const fetchSystemStats = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch all data in parallel
        const [usersResponse, toursResponse, contentResponse] = await Promise.allSettled([
          api.get('/auth/users/'),
          api.get('/tours/tours/'),
          api.get('/content/contents/'),
        ]);
        // Extract counts from responses
        const totalUsers = usersResponse.status === 'fulfilled' ? getCountFromResponse(usersResponse.value) : 0;
        const totalTours = toursResponse.status === 'fulfilled' ? getCountFromResponse(toursResponse.value) : 0;
        const totalContent = contentResponse.status === 'fulfilled' ? getCountFromResponse(contentResponse.value) : 0;
        // Fetch robots count from NHB API
        let totalRobots = 0;
        try {
          const robotsResponse = await fetch('/api/robots/proxy/status/?include_battery=true&include_location=true', {
            method: 'GET',
            headers: {
              accept: 'application/json',
            },
          });
          if (robotsResponse.ok) {
            const robotsData = await robotsResponse.json();
            if (robotsData.success && robotsData.data?.robots) {
              totalRobots = robotsData.data.robots.length;
            }
          }
        } catch (err) {
          console.warn('Failed to fetch robots count:', err);
          totalRobots = 0;
        }
        setSystemStats({
          totalRobots,
          totalUsers,
          totalTours,
          totalContent,
        });
      } catch (err) {
        console.error('Error fetching system stats:', err);
        setError('Failed to load system statistics');
      } finally {
        setLoading(false);
      }
    };
    fetchSystemStats();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'robot':
        return <RobotIcon />;
      case 'user':
        return <UserIcon />;
      case 'tour':
        return <TourIcon />;
      case 'content':
        return <ContentIcon />;
      default:
        return <TimeIcon />;
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        System Overview
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome back, {user?.username}! Here&apos;s what&apos;s happening across all museums in your robot dashboard.
      </Typography>
      {/* System Statistics: GLOBAL ONLY */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 3,
            mb: 4,
            justifyContent: 'center',
          }}
        >
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <RobotIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" component="div">
                {systemStats.totalRobots}
              </Typography>
              <Typography color="text.secondary">Total Robots</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <UserIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" component="div">
                {systemStats.totalUsers}
              </Typography>
              <Typography color="text.secondary">Total Users</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TourIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" component="div">
                {systemStats.totalTours}
              </Typography>
              <Typography color="text.secondary">Total Tours</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ContentIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" component="div">
                {systemStats.totalContent}
              </Typography>
              <Typography color="text.secondary">Content Items</Typography>
            </CardContent>
          </Card>
        </Box>
      )}
      {/* Recent Activities: GLOBAL ONLY */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Activities
          </Typography>
          {activitiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : recentActivities.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No recent activities found
              </Typography>
            </Box>
          ) : (
            <List>
              {recentActivities.map((activity, index: number) => (
                <React.Fragment key={activity.id}>
                  <ListItem>
                    <ListItemIcon>{getActivityIcon(activity.type)}</ListItemIcon>
                    <ListItemText primary={activity.message} secondary={activity.timestamp} />
                    <Chip
                      label={activity.status}
                      color={getStatusColor(activity.status || 'info') as 'success' | 'error' | 'warning' | 'info'}
                      size="small"
                    />
                  </ListItem>
                  {index < recentActivities.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardOverview;
